// Corporate Portal — Workforce / Employee Reports
//
// Lists every patient registered under the corporate client's
// CUSTOMERID in IT Dose — one card per visit (same shape the medical
// portal's PatientReports page uses) — and opens the existing
// ReportDetailModal on click so the corporate user can pull each
// employee's reports without us building any new IT Dose endpoint.
//
// Data path:
//   useGetSalesByCustomerQuery({ customerId: user.client_id, page })
//     → /api/portal/patient-reports/by-customer?customerId=…&page=…
//        → backend in-memory cache of the IT Dose S3 dump.
//
// Reuses:
//   - groupByVisit / prettyDate / cleanName helpers from ../medical/shared
//   - ReportDetailModal from ../medical (renders the visit detail +
//     the IT Dose PDF launch button)
//
// Styling matches the rest of the Corporate Portal (forest + gold
// tokens, not the medical portal's teal).

import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuth } from '../../../context/AuthContext';
import { useGetSalesByCustomerQuery } from '../../../store/salesApi';
import { groupByVisit, prettyDate } from '../medical/shared';
import ReportDetailModal from '../medical/ReportDetailModal';
import { prefetchReportPdfs, prefetchVisit } from '../../../utils/prefetchReports';

// Forest/gold tokens — mirror CorporateDashboard.js so the Workforce
// page reads as part of the same portal. Kept local so this file
// stays self-contained.
const C = {
  forest:   '#1A3A2A',
  forestD:  '#0F2419',
  forestXL: '#3D7A52',
  gold:     '#C8A24B',
  goldD:    '#a8843a',
  ink:      '#19211d',
  slate:    '#5a6862',
  slateL:   '#8a958f',
  line:     '#e2e7e3',
  lineD:    '#cfd6d1',
  paper:    '#ffffff',
  wash:     '#f5f8f6',
  wash2:    '#eef3ef',
  ok:       '#1f7a4d',
  okBg:     '#e7f3ec',
  due:      '#a23b2e',
};

// MUI input override that picks up the forest palette.
const filterInputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: 13.5, bgcolor: C.wash, borderRadius: '8px',
    '& fieldset': { borderColor: C.lineD, borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: C.lineD },
    '&.Mui-focused fieldset': { borderColor: C.forestXL },
  },
  '& input, & .MuiSelect-select': { padding: '10px 12px' },
  '& .MuiInputLabel-root': { fontSize: 12.5, color: C.slate },
  '& .MuiInputLabel-root.Mui-focused': { color: C.forest },
};

const StatusPill = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const map = s.includes('active') && !s.includes('de')
    ? { bg: C.okBg, fg: C.ok, label: 'Active' }
    : s.includes('hold')
      ? { bg: '#fbf2da', fg: '#9a6b00', label: 'On hold' }
      : { bg: C.wash2, fg: C.slate, label: status || '—' };
  return (
    <Box sx={{
      display: 'inline-block', fontSize: 10, fontWeight: 700,
      px: 1.2, py: '3px', borderRadius: '5px',
      bgcolor: map.bg, color: map.fg, whiteSpace: 'nowrap',
    }}>
      {map.label}
    </Box>
  );
};

const EmployeeCard = ({ visit, onOpen }) => (
  <Box
    onClick={() => onOpen(visit)}
    // Hover / touch is a strong signal the user is about to click —
    // start warming this visit's PDF in the background NOW so by the
    // time they actually open the modal + click Download PDF the
    // backend cache is hot. Module-scoped dedupe means rapid
    // hover-flutter doesn't fire repeat fetches.
    onMouseEnter={() => prefetchVisit(visit?.visitNo)}
    onTouchStart={() => prefetchVisit(visit?.visitNo)}
    sx={{
      cursor: 'pointer', bgcolor: C.paper,
      border: `1px solid ${C.line}`, borderRadius: '12px', p: '16px 18px',
      transition: '0.16s',
      '&:hover': {
        borderColor: C.forest,
        boxShadow: '0 6px 22px rgba(15,36,25,0.08)',
        transform: 'translateY(-1px)',
      },
    }}
  >
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: C.forest, lineHeight: 1.25 }}>
          {visit.patient || '—'}
        </Typography>
        <Typography sx={{ fontSize: 11, color: C.slateL, mt: 0.25 }}>
          UHID {visit.uhid || '—'} · {visit.age || '—'} · {visit.gender || '—'}
        </Typography>
      </Box>
      <StatusPill status={visit.status} />
    </Stack>

    <Box sx={{ mt: 1.5, borderTop: `1px dashed ${C.line}`, pt: 1.25 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
        <Typography sx={{ fontSize: 10.5, color: C.slateL, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Visit {visit.visitNo || '—'}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: C.slate }}>
          {prettyDate(visit.billDate) || '—'}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 12, color: C.ink, lineHeight: 1.4 }}>
        <b style={{ color: C.forest }}>{visit.tests.length}</b>{' '}
        {visit.tests.length === 1 ? 'test' : 'tests'} —{' '}
        {visit.tests.slice(0, 3).map((t) => t.name).filter(Boolean).join(', ')}
        {visit.tests.length > 3 && ` + ${visit.tests.length - 3} more`}
      </Typography>
    </Box>

    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.25, color: C.forest }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>View report</Typography>
      <OpenInNewIcon sx={{ fontSize: 13 }} />
    </Stack>
  </Box>
);

const EmployeeSkeleton = () => (
  <Box sx={{
    bgcolor: C.paper, border: `1px solid ${C.line}`,
    borderRadius: '12px', p: '16px 18px',
  }}>
    <Skeleton width="60%" height={20} />
    <Skeleton width="40%" height={14} sx={{ mt: 0.5 }} />
    <Box sx={{ mt: 1.5, borderTop: `1px dashed ${C.line}`, pt: 1.25 }}>
      <Skeleton width="35%" height={12} />
      <Skeleton width="85%" height={14} sx={{ mt: 1 }} />
    </Box>
  </Box>
);

const Workforce = () => {
  const { user } = useAuth();
  const clientId = user?.client_id;

  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [clientId]);

  const { data, isLoading, isFetching } = useGetSalesByCustomerQuery(
    { customerId: clientId, page },
    { skip: !clientId },
  );

  const visits      = useMemo(() => groupByVisit(data?.data || []), [data]);
  const showSkeletons = isLoading || isFetching;

  // Warm the backend PDF cache for the top visible cards as soon as
  // they land. The click flow later hits an already-cached PDF and
  // opens in <1 second instead of 4-8s.
  useEffect(() => {
    if (visits.length > 0) prefetchReportPdfs(visits);
  }, [visits]);

  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return visits;
    return visits.filter((v) => {
      const blob = `${v.patient} ${v.uhid} ${v.visitNo} ${v.tests.map((t) => t.name).join(' ')}`.toLowerCase();
      return blob.includes(q);
    });
  }, [visits, search]);

  // Detail modal — reused as-is from the medical portal. No Raise
  // Query CTA in the corporate flow yet, so we hand it a no-op.
  const [modal, setModal] = useState({ open: false, id: null, visit: null });
  const closeModal = () => setModal({ open: false, id: null, visit: null });
  const openReport = (v) => setModal({ open: true, id: v.visitNo, visit: v });

  const meta = data?.meta || {};
  const totalPages   = meta.totalPages   || 0;
  const totalMatched = meta.totalMatched || 0;
  const currentPage  = meta.currentPage  || page;

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        gap={1.5}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: C.forest, letterSpacing: '-0.2px' }}>
            Workforce Reports
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: C.slate, mt: 0.25 }}>
            Every employee visit booked under your corporate ID — click any card to see the full report.
            {totalPages > 0 && (
              <> &nbsp;•&nbsp; Page {currentPage} of {totalPages} · {totalMatched.toLocaleString('en-IN')} total visits</>
            )}
          </Typography>
        </Box>
      </Stack>

      <TextField
        fullWidth size="small"
        placeholder="Search by name, UHID, visit no., or test…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ ...filterInputSx, mb: 2.5 }}
      />

      <Grid container spacing={2}>
        {showSkeletons && Array.from({ length: 9 }).map((_, i) => (
          <Grid item key={`sk-${i}`} xs={12} sm={6} md={4}>
            <EmployeeSkeleton />
          </Grid>
        ))}

        {!showSkeletons && filtered.map((v) => (
          <Grid item key={`${v.visitNo}-${v.billNo}`} xs={12} sm={6} md={4}>
            <EmployeeCard visit={v} onOpen={openReport} />
          </Grid>
        ))}

        {!showSkeletons && filtered.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{
              py: 6, textAlign: 'center', color: C.slate,
              bgcolor: C.paper, border: `1px solid ${C.line}`, borderRadius: '12px',
            }}>
              {visits.length === 0
                ? (clientId
                    ? 'No employees have been registered under this corporate ID yet. Once HR adds them as patients in IT Dose, their visits will appear here.'
                    : 'Your account is missing a Client ID — contact admin to link one.')
                : 'No employees match the current search.'}
            </Box>
          </Grid>
        )}
      </Grid>

      {totalPages > 1 && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ mt: 3.5 }}
        >
          <Button
            onClick={() => { setPage((p) => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            disabled={isFetching || currentPage <= 1}
            startIcon={<ChevronLeftIcon />}
            sx={{
              fontSize: 12, fontWeight: 700, color: C.forest, textTransform: 'none',
              bgcolor: C.wash, border: `1px solid ${C.lineD}`, borderRadius: '8px',
              px: 1.5, py: 0.75,
              '&:hover': { bgcolor: C.wash2, borderColor: C.forest },
              '&.Mui-disabled': { color: C.slateL, bgcolor: C.wash2 },
            }}
          >
            Prev
          </Button>
          <Typography sx={{
            fontSize: 12, fontWeight: 700, color: C.forest,
            bgcolor: C.gold, borderRadius: '8px', px: 1.5, py: 0.85,
            minWidth: 72, textAlign: 'center',
          }}>
            {currentPage} / {totalPages}
          </Typography>
          <Button
            onClick={() => { setPage((p) => p + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            disabled={isFetching || currentPage >= totalPages}
            endIcon={<ChevronRightIcon />}
            sx={{
              fontSize: 12, fontWeight: 700, color: C.forest, textTransform: 'none',
              bgcolor: C.wash, border: `1px solid ${C.lineD}`, borderRadius: '8px',
              px: 1.5, py: 0.75,
              '&:hover': { bgcolor: C.wash2, borderColor: C.forest },
              '&.Mui-disabled': { color: C.slateL, bgcolor: C.wash2 },
            }}
          >
            Next
          </Button>
        </Stack>
      )}

      <ReportDetailModal
        open={modal.open}
        onClose={closeModal}
        reportId={modal.id}
        visit={modal.visit}
        onRaiseQuery={() => closeModal()}
      />

      <Typography sx={{
        mt: 4, fontSize: 11, color: C.slateL,
        borderTop: `1px solid ${C.line}`, pt: 1.75, lineHeight: 1.6,
      }}>
        Data sourced from IT Dose under your corporate Customer ID. Each visit shows the tests
        ordered, status, and visit metadata; clicking through opens the report PDF (signed off
        by the Certifying Surgeon where applicable) and the full per-test breakdown.
      </Typography>
    </Box>
  );
};

export default Workforce;
